import { InputField } from "@/components/input-field/InputField";
import { SelectComponent } from "@/components/select/Select";
import { Sucursal } from "@/services/accounts.service";
import { ActiveDirectoryUser } from "@/pages/role-asignation/schema/role-assignation.schema";
import { AppColors } from "@/styles/colors";
import { Card, Field, Grid, GridItem, Text } from "@chakra-ui/react";
import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormSetValue,
} from "react-hook-form";
import { Dispatch, SetStateAction, useMemo, useRef } from "react";
import {
  AsyncAutocompleteSelect,
  Option,
} from "@/components/select/AsyncAutocompleteSelect";
import {
  getOptionsHandler,
  loadOptionsHandler,
} from "@/pages/role-asignation/schema/handler-functions";

interface Props {
  errors: FieldErrors<any>;
  control: Control<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  setLoadingADUsers: Dispatch<SetStateAction<boolean>>;
  handleSucursalSelection?: (e: string[]) => void;
  noUser?: boolean;
  loadingAD?: boolean;
  loadingSucursales?: boolean;
  loadingOperation?: boolean;
  sucursales?: Sucursal[] | null;
  ADUsers?: ActiveDirectoryUser[] | null;
}

export const UserSelectionCard = ({
  errors,
  control,
  noUser = false,
  loadingAD,
  loadingSucursales,
  loadingOperation,
  sucursales,
  setValue,
  handleSucursalSelection,
  setLoadingADUsers,
  ADUsers,
}: Props) => {
  const adUserById = useMemo(() => {
    const map = new Map<string, ActiveDirectoryUser>();
    for (const u of ADUsers ?? []) {
      if (u?.id) map.set(u.id, u);
    }
    return map;
  }, [ADUsers]);

  const handleUserSelection = (user?: Option) => {
    const id = String(user?.value ?? "");
    const currentUser = adUserById.get(id);

    setValue(
      "full_name",
      `${currentUser?.givenName ?? ""} ${currentUser?.surname ?? ""}`,
    );
    setValue("mail", currentUser?.mail ?? "");
  };

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const getADOptions = async (
    inputValue: string,
  ): Promise<
    {
      label: string;
      value: string;
    }[]
  > => {
    return getOptionsHandler({
      inputValue,
    });
  };

  const loadADOptions = (inputValue: string) => {
    return loadOptionsHandler({
      getOptions: getADOptions,
      inputValue,
      setLoading: setLoadingADUsers,
      timeoutRef,
    });
  };

  const userOptionsMemo = useMemo(() => {
    return (
      ADUsers?.map((user) => ({
        label: `${user.displayName} (${user.mail})`,
        value: user.id,
      })) || []
    );
  }, [ADUsers]);

  return (
    <Card.Root maxW="full" marginTop="30px">
      <Card.Body
        paddingX="48px"
        paddingY="30px"
        borderRadius="8px"
        border={`1px solid ${AppColors.WHITE_BORDER_1}`}
      >
        <Grid templateColumns={`repeat(${noUser ? 2 : 3}, 1fr)`} gap="32px">
          {!noUser && (
            <GridItem>
              <Field.Root
                invalid={!!errors?.user?.message}
                display="flex"
                flexDir="column"
              >
                <Field.Label>
                  <Text fontWeight={600}>Usuario (Active Directory)</Text>

                  <Text fontWeight={600} color={AppColors.REQUIRED}>
                    *
                  </Text>
                </Field.Label>
                <AsyncAutocompleteSelect
                  control={control}
                  defaultOptions={userOptionsMemo}
                  loadOptions={loadADOptions}
                  name="user"
                  placeholder="Buscar..."
                  isLoading={loadingAD}
                  handleChange={handleUserSelection}
                  closeMenuOnSelect
                  triggerWithLength={false}
                  disabled={loadingOperation}
                  cacheOptions={false}
                />
              </Field.Root>
            </GridItem>
          )}
          <GridItem>
            <InputField
              inputName="full_name"
              labelText="Nombre y Apellido"
              placeholder=""
              disabled
              control={control}
              height="40px"
              minHeight="40px"
              required
            />
          </GridItem>
          <GridItem>
            <Field.Root
              invalid={!!errors?.sucursal?.message}
              display="flex"
              flexDir="column"
              gap="12px"
            >
              <SelectComponent
                labelText="Sucursales"
                name="sucursales"
                multiple
                control={control}
                minHeight="40px"
                menuMaxHeight={{
                  xs: "350px",
                  sm: "420px",
                  md: "500px",
                  lg: "600px",
                  xl: "1000px",
                }}
                required
                items={
                  sucursales?.map((sucursal: Sucursal) => ({
                    label: sucursal.nombre,
                    value: sucursal.clave,
                  })) || []
                }
                disabled={loadingSucursales || loadingOperation}
                handleChange={handleSucursalSelection}
                loading={loadingSucursales}
                fullWidth
              />
            </Field.Root>
          </GridItem>
        </Grid>
      </Card.Body>
    </Card.Root>
  );
};

